<template>
  <a
    v-if="productHasMedia && !onPlp"
    :href="product.context_url"
    :class="imageWrapperClasses"
    :aria-label="product.title"
  >
      <div
        v-if="productHasMultipleMedia"
        class="product-item__images" :style="getHeight"
      >
        <div class="product-item__sizer">
          <VImage
            :alt-text="product.featured_image.alt"
            :aspect-ratio="product.featured_image.aspect_ratio"
            :disable-aspect-ratio="includesAspectRatio"
            :height="product.featured_image.height"
            :placeholder-src="product.featured_image.placeholder_src"
            :src="product.featured_image.src"
            :width="product.featured_image.width"
          />
        </div>
        <div class="product-item__image first">
          <VImage
            :alt-text="product.featured_image.alt"
            :aspect-ratio="product.featured_image.aspect_ratio"
            :disable-aspect-ratio="includesAspectRatio"
            :height="product.featured_image.height"
            :placeholder-src="product.featured_image.placeholder_src"
            :src="product.featured_image.src"
            :width="product.featured_image.width"
          />
        </div>
        <div class="product-item__image not-first">
          <VImage
            :alt-text="product.media[1].alt"
            :aspect-ratio="product.media[1].aspect_ratio"
            disable-aspect-ratio
            :height="product.media[1].height"
            :placeholder-src="product.media[1].placeholder_src"
            :src="product.media[1].src"
            :width="product.media[1].width"
          />
        </div>
      </div>
    
    <div
      v-else
      class="product-item__image"
    >
      <VImage
        :alt-text="featuredImage.alt"
        :aspect-ratio="featuredImage.aspect_ratio"
        :disable-aspect-ratio="includesAspectRatio"
        :height="featuredImage.height"
        :placeholder-src="featuredImage.placeholder_src"
        :src="featuredImage.src"
        :width="featuredImage.width"
      />
    </div>
  </a>
  <a
    v-else-if="productHasMedia && onPlp"
    v-touch:swipe.left="slideNext"
    v-touch:swipe.right="slidePrevious"
    :href="product.context_url"
    :class="imageWrapperClasses"
    @focus="onGalleryFocus"
  >
    <transition
      v-if="productHasMultipleMedia"
      name="fade"
    >
      <div
        v-show="imageHovered || isMobile"
        class="product-item__images-navigation"
      >
        <button
          type="button"
          class="carousel__navigation carousel__previous-button"
          :aria-label="arrowPreviousLabel"
          tabindex="0"
          @click.prevent="slidePrevious"
          @keyup.enter="slidePrevious"
        >
          <icon
            data-vue-root="Icon"
            name="chevron-right-thin"
            size="12px"
            fill="transparent"
            stroke="#C13B2A"
          />
        </button>
        <button
          type="button"
          class="carousel__navigation carousel__next-button"
          :aria-label="arrowNextLabel"
          tabindex="0"
          @click.prevent="slideNext"
          @keyup.enter="slideNext"
        >
          <icon
            data-vue-root="Icon"
            name="chevron-right-thin"
            size="12px"
            fill="transparent"
            stroke="#C13B2A"
          />
        </button>
      </div>
    </transition>
    <div
      v-if="productHasMultipleMedia"
      class="product-item__images" 
      :style="getHeight"
    >
    <transition-group :name="transitionName" v-if="productHasMultipleMedia">
      <VImage
        v-for="(media, index) in mediaImages"
        v-show="activeImage === index"
        :key="index"
        :alt-text="media.alt"
        :aspect-ratio="media.aspect_ratio"
        :disable-aspect-ratio="includesAspectRatio"
        :enable-lazyload="false"
        :height="media.height"
        :placeholder-src="media.placeholder_src"
        :src="media.src"
        :width="media.width"
      />
    </transition-group>
    </div>
    <div
      v-else
      class="product-item__image"
    >
      <VImage
        :alt-text="featuredImage.alt"
        :aspect-ratio="featuredImage.aspect_ratio"
        :disable-aspect-ratio="includesAspectRatio"
        :height="featuredImage.height"
        :placeholder-src="featuredImage.placeholder_src"
        :src="featuredImage.src"
        :width="featuredImage.width"
      />
    </div>
  </a>
  <a
    v-else
    :href="product.context_url"
    :class="imageWrapperClasses"
    :aria-label="product.title"
  >
    <slot name="quickShopBtn" />
    <div class="product-item__image">
      <slot name="placeholderMedia" />
    </div>
  </a>
</template>

<script>
import VImage from 'scripts/components/basic/VImage.vue';
import Icon from 'scripts/components/basic/Icon.vue';

export default {
  components: {
    VImage,
    Icon,
  },

  props: {
    product: {
      type: Object,
      required: true,
    },

    imageHovered: {
      type: Boolean,
      default: false,
    },

    includesAspectRatio: {
      type: Boolean,
      default: false,
    },

    onPlp: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeImage: 0,
      calculatedHeight: 0,
      windowWidth: window.innerWidth,
      transitionName: '',
      currentProduct: this.product
    };
  },

  computed: {
    arrowNextLabel() {
      return window.theme.strings.arrowNext;
    },

    arrowPreviousLabel() {
      return window.theme.strings.arrowPrevious;
    },

    featuredImage() {
      return this.product.featured_image.preview_image;
    },

    imageWrapperClasses() {
      return {
        'product-item__image-wrapper db': true
      };
    },

    isMobile() {
      return ['mobile', 'tablet'].includes(this.$mq);
    },

    mediaImages() {
      return this.product.media.filter((media) => media.type === 'image');
    },

    productHasMedia() {
      return this.productHasMultipleMedia;
    },

    productHasMultipleMedia() {
      return this.product.media.length > 1;
    },
    getHeight() {
      return {
        'min-height': `${this.calculatedHeight}px`,
      };
    }
  },

  updated() {
    if (this.activeImage > this.mediaImages.length - 1) {
      this.activeImage = 0;
    }
  },

  methods: {
    onGalleryFocus() {
      this.$emit('focused', true);
    },

    slidePrevious() {
      this.activeImage -= 1;
      this.transitionName = "slide-prev"

      if (this.activeImage < 0) {
        this.activeImage = this.mediaImages.length - 1;
      }
    },

    slideNext() {
      this.transitionName = "slide-next"
      this.activeImage += 1;
      if (this.activeImage === this.mediaImages.length) {
        this.activeImage = 0;
      }
    },
    handleResize() {
      const newWidth = window.innerWidth;
      if (newWidth != this.windowWidth) {
        this.calculatedHeight = this.$el.querySelector('.image').offsetHeight;
        this.windowWidth = newWidth;
      }
      
    }
  },
  mounted() {
    this.calculatedHeight = this.$el.querySelector('.image').offsetHeight;
    window.addEventListener('resize', this.handleResize)
  }
};
</script>

<style></style>
